import React from 'react';
import { IconChat, IconFile } from '../icons';
import {
  formatPortalDate,
  formatPortalDateTime,
  isHiddenPortalRequirementStatus,
  meetingUrlOf,
  PortalRequirement,
  PortalRequirementResource,
} from '../api/portalService';
import { isOleBookedAppointment } from '../modals/scheduleSlots';
import { usePortalCopy, usePortalUi } from '../PortalUi';
import { PortalButton, PortalStatus } from '../ui';
import { formatRequirementMaxAge } from './requirementMaxAge';
import { RequirementRow } from './RequirementRow';

export type RequirementsListProps = {
  items: PortalRequirement[];
  kind: 'initial' | 'additional';
  loading?: boolean;
  error?: Error | null;
  settled?: boolean;
  onRetry?: () => void;
  uploadSpec: string;
  replySpec: string;
};

function matchesKind(item: PortalRequirement, kind: 'initial' | 'additional'): boolean {
  const value = (item.kind || '').toLowerCase();
  if (!value) return kind === 'initial';
  return value === kind;
}

function fileNames(item: PortalRequirement): string {
  return item.files.map((file) => file.originalName).filter(Boolean).join(', ');
}

/**
 * OUW-364: el botón del recurso dice lo que configuró el catálogo («Descargar
 * Informe»). Sin título, un archivo se descarga y cualquier otro recurso se abre.
 */
function resourceLabel(resource: PortalRequirementResource, t: ReturnType<typeof usePortalCopy>): string {
  const title = resource.title.trim();
  if (title) return title;
  return resource.kind === 'file' ? t('reqDownload') : t('reqOpen');
}

export function RequirementsList({
  items,
  kind,
  loading,
  error,
  settled,
  onRetry,
  uploadSpec,
  replySpec,
}: RequirementsListProps) {
  const t = usePortalCopy();
  const { openLiveUpload, openLiveReply, openLiveSchedule, deliverLiveRequirement } = usePortalUi();
  const visible = items.filter((item) => matchesKind(item, kind) && !isHiddenPortalRequirementStatus(item.status));

  if (loading && items.length === 0) {
    return (
      <div className="processing-row" style={{ marginTop: 16 }}>
        <span className="processing-spinner" aria-hidden="true" />
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="acts" style={{ marginTop: 16 }}>
        <p>{t('reqLoadError')}</p>
        {onRetry ? (
          <PortalButton variant="secondary" size="sm" label={t('retry')} onClick={onRetry} />
        ) : null}
      </div>
    );
  }

  if (settled && visible.length === 0) {
    return <p style={{ marginTop: 16 }}>{t('reqEmpty')}</p>;
  }

  return (
    <>
      {visible.map((item) => {
        const done = item.status === 'completed';
        const note = item.noteToClient.trim();
        const detail = item.clientDetail.trim() || item.resources[0]?.title || '';
        const when = item.lastTransitionAt ? formatPortalDate(item.lastTransitionAt) : '';
        const names = fileNames(item);
        const canUpload = item.clientActions.includes('upload');
        const canRespond = item.clientActions.includes('respond');
        // OUW-260: la cita que agenda Olé. Tiene tres momentos y el cliente
        // tiene que poder distinguirlos de un vistazo: no propuso nada,
        // propuso y espera, o ya hay cita confirmada.
        // OUW-364: solo las citas que coordina Olé; el chequeo con médico propio
        // no tiene con quién agendar aunque su copia del catálogo diga schedule.
        const canSchedule = item.clientActions.includes('schedule') && isOleBookedAppointment(item);
        const booked = canSchedule && Boolean(item.scheduledAt);
        const proposed = canSchedule && !booked && Boolean(item.availability);
        const maxAge = done ? null : formatRequirementMaxAge(item.maxAgeDays, t);
        const body = (
          <>
            {note ? (
              <div className="uw-note">
                <b>{t('uwNote')}</b>
                {item.noteToClient}
              </div>
            ) : null}
            {done && when ? <p>{when}{names ? ` · ${names}` : ''}</p> : null}
            {booked ? (
              <p>
                <b>{t('scheduleBooked')}</b>
                {` · ${formatPortalDateTime(item.scheduledAt as string)}`}
              </p>
            ) : null}
            {proposed ? (
              <p>
                <b>{t('scheduleProposed')}</b>
                {` · ${t('scheduleProposedDetail')}`}
              </p>
            ) : null}
            {!done && !booked && !proposed && detail ? <p>{detail}</p> : null}
            {maxAge ? <p>{maxAge}</p> : null}
          </>
        );

        let action: React.ReactNode = null;
        if (done) {
          action = <PortalStatus tone="soft" variant="active" label={t('reqInReview')} />;
        } else {
          action = (
            <>
              {item.resources.map((resource) => (
                <PortalButton
                  key={resource.url}
                  size="sm"
                  variant="secondary"
                  className="act-do"
                  label={resourceLabel(resource, t)}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ))}
              {booked && meetingUrlOf(item.meetingUrl) ? (
                <PortalButton
                  size="sm"
                  className="act-do"
                  label={t('scheduleJoin')}
                  data-spec="o-schedule-join"
                  data-wired
                  href={meetingUrlOf(item.meetingUrl) as string}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ) : null}
              {canSchedule && !booked ? (
                <PortalButton
                  size="sm"
                  variant={proposed ? 'secondary' : 'primary'}
                  className="act-do"
                  label={proposed ? t('scheduleCtaAgain') : t('scheduleCta')}
                  data-spec="o-schedule"
                  data-wired
                  onClick={() => openLiveSchedule(item)}
                />
              ) : null}
              {canUpload ? (
                <PortalButton
                  size="sm"
                  className="act-do"
                  label={t('upload')}
                  data-spec={uploadSpec}
                  data-wired
                  onClick={() => openLiveUpload(item)}
                />
              ) : null}
              {canRespond ? (
                <PortalButton
                  size="sm"
                  className="act-do"
                  label={t('reply')}
                  data-spec={replySpec}
                  data-wired
                  onClick={() => openLiveReply(item)}
                />
              ) : null}
              {item.canDeliver && !canUpload && !canRespond && !canSchedule ? (
                <PortalButton
                  size="sm"
                  className="act-do"
                  label={t('send')}
                  data-wired
                  onClick={() => deliverLiveRequirement(item)}
                />
              ) : null}
            </>
          );
        }

        return (
          <RequirementRow
            key={item.id}
            id={`req-${item.id}`}
            done={done}
            icon={canRespond && !canUpload ? <IconChat /> : <IconFile />}
            title={item.clientLabel || item.id}
            body={body}
            action={action}
          />
        );
      })}
    </>
  );
}

export function requirementsHeadingCount(items: PortalRequirement[], kind: 'initial' | 'additional'): number {
  return items.filter((item) => matchesKind(item, kind) && item.status === 'requested').length;
}
